import {getCopilotSpacePath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {CopilotSpace, IndexCopilotSpace} from '@github-ui/copilot-chat-types'
import {useNavigate} from '@github-ui/use-navigate'
import {
  CopyIcon,
  KebabHorizontalIcon,
  PencilIcon,
  ReportIcon,
  ShareIcon,
  StarFillIcon,
  StarIcon,
  TrashIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, ConfirmationDialog, IconButton, Spinner} from '@primer/react'
import type {ReactNode} from 'react'
import {useEffect, useMemo, useRef, useState} from 'react'

import {useFetchCopilotSpace, useFetchMenuActions, useReportUrl} from '../hooks'
import {useStarCopilotSpace} from '../hooks/use-star-copilot-space-no-chat-state'
import {CreateSpaceDialog} from './CreateSpaceDialog'
import {ReportSpaceDialog} from './ReportSpaceDialog'

interface SpaceMenuProps {
  space: IndexCopilotSpace
  onDelete: () => Promise<void>
  spacesBasePath?: string
  renderDuplicateDialog?: (space: CopilotSpace, closeDialog: () => void) => ReactNode
  renderShareDialog?: (closeDialog: () => void) => ReactNode
  showEdit?: boolean
  showStar?: boolean
  onAction?: (target: string) => void
}

export function SpaceMenu({
  space,
  onDelete,
  spacesBasePath,
  renderDuplicateDialog,
  renderShareDialog,
  showEdit = true,
  showStar = false,
  onAction,
}: SpaceMenuProps) {
  const [visibleDialog, setVisibleDialog] = useState<'delete' | 'share' | 'duplicate' | 'report' | null>(null)
  const [loading, setLoading] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [shouldFetchMenuActions, setShouldFetchMenuActions] = useState(false)
  const [focusRequest, setFocusRequest] = useState(0)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const {starCopilotSpace, isLoading: isStarring} = useStarCopilotSpace({id: space.id, owner: space.owner})
  const {data: fullSpace, isLoading: isLoadingFullSpace} = useFetchCopilotSpace(
    visibleDialog === 'duplicate' ? space : null,
  )
  const {
    data: menuActions,
    isLoading: isLoadingMenuActions,
    error: menuActionsError,
  } = useFetchMenuActions(shouldFetchMenuActions ? space.owner : '', shouldFetchMenuActions ? space.id : -1)

  const permissions = useMemo(
    () =>
      menuActions
        ? {
            editable: menuActions.editable,
            adminable: menuActions.adminable,
            shareable: menuActions.shareable,
            reportable: menuActions.reportable,
          }
        : {
            editable: space.editable,
            adminable: space.adminable,
            shareable: space.shareable,
            reportable: false,
          },
    [menuActions, space],
  )
  const reportUrl = useReportUrl({id: space.id, owner: space.owner}, spacesBasePath)

  if (visibleDialog === 'duplicate' && menuOpen && !isLoadingFullSpace && fullSpace) setMenuOpen(false)

  const closeDialog = () => {
    setVisibleDialog(null)
    setFocusRequest(request => request + 1)
  }
  const closeShareDialog = () => {
    onAction?.('COPILOT_SPACE_SHARE_DIALOG_CLOSE')
    closeDialog()
  }
  const closeDuplicateDialog = () => {
    onAction?.('COPILOT_SPACE_DUPLICATE_DIALOG_CLOSE')
    closeDialog()
  }

  useEffect(() => {
    if (focusRequest === 0) return
    anchorRef.current?.focus()
  }, [focusRequest])

  const applyUpdate = async (update: () => Promise<void>) => {
    closeDialog()
    setLoading(true)
    try {
      await update()
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <ActionMenu
        anchorRef={anchorRef}
        open={menuOpen}
        onOpenChange={open => {
          setMenuOpen(open)
          if (open) setShouldFetchMenuActions(true)
        }}
      >
        <ActionMenu.Anchor>
          <IconButton
            loading={loading}
            icon={KebabHorizontalIcon}
            aria-label={`Manage ${space.name}`}
            variant="invisible"
            onClick={() => onAction?.('COPILOT_SPACE_CONTEXT_MENU')}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay side="outside-bottom" align="end">
          {isLoadingMenuActions ? (
            <ActionList>
              <ActionList.Item>
                <Spinner size="small" />
              </ActionList.Item>
            </ActionList>
          ) : menuActionsError ? (
            <ActionList>
              <ActionList.Item disabled>Failed to load menu options</ActionList.Item>
            </ActionList>
          ) : (
            <ActionList>
              {showEdit && permissions.editable && (
                <ActionList.Item
                  onSelect={() => {
                    onAction?.('COPILOT_SPACE_CONTEXT_MENU_EDIT')
                    void navigate(getCopilotSpacePath(space, spacesBasePath))
                  }}
                >
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  Edit
                </ActionList.Item>
              )}
              {permissions.shareable && renderShareDialog && (
                <ActionList.Item
                  onSelect={() => {
                    onAction?.('COPILOT_SPACE_CONTEXT_MENU_SHARE')
                    setVisibleDialog('share')
                  }}
                >
                  <ActionList.LeadingVisual>
                    <ShareIcon />
                  </ActionList.LeadingVisual>
                  Share
                </ActionList.Item>
              )}
              {showStar && (
                <ActionList.Item onSelect={() => void starCopilotSpace({starred: !space.starred})} loading={isStarring}>
                  <ActionList.LeadingVisual>{space.starred ? <StarFillIcon /> : <StarIcon />}</ActionList.LeadingVisual>
                  <span>{space.starred ? 'Unstar' : 'Star'}</span>
                </ActionList.Item>
              )}
              {(permissions.adminable || permissions.editable) && (
                <ActionList.Item
                  onSelect={() => {
                    onAction?.('COPILOT_SPACE_CONTEXT_MENU_DUPLICATE')
                    setVisibleDialog('duplicate')
                  }}
                  loading={isLoadingFullSpace && visibleDialog === 'duplicate'}
                >
                  <ActionList.LeadingVisual>
                    <CopyIcon />
                  </ActionList.LeadingVisual>
                  Duplicate
                </ActionList.Item>
              )}
              {permissions.reportable && (
                <ActionList.Item
                  onSelect={() => {
                    onAction?.('COPILOT_SPACE_CONTEXT_MENU_REPORT')
                    setVisibleDialog('report')
                  }}
                >
                  <ActionList.LeadingVisual>
                    <ReportIcon />
                  </ActionList.LeadingVisual>
                  Report content
                </ActionList.Item>
              )}
              {permissions.adminable && (
                <ActionList.Item
                  variant="danger"
                  onSelect={() => {
                    onAction?.('COPILOT_SPACE_CONTEXT_MENU_DELETE')
                    setVisibleDialog('delete')
                  }}
                >
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  Delete
                </ActionList.Item>
              )}
            </ActionList>
          )}
        </ActionMenu.Overlay>
      </ActionMenu>

      {visibleDialog === 'delete' && (
        <DeleteSpaceDialog
          onCancel={closeDialog}
          onConfirm={() => {
            onAction?.('COPILOT_SPACE_LIST_ACTION_DELETE')
            return applyUpdate(onDelete)
          }}
          returnFocusRef={anchorRef}
        />
      )}
      {visibleDialog === 'share' && renderShareDialog?.(closeShareDialog)}
      {visibleDialog === 'duplicate' &&
        fullSpace &&
        (renderDuplicateDialog ? (
          renderDuplicateDialog(fullSpace, closeDuplicateDialog)
        ) : (
          <CreateSpaceDialog
            dialogTitle="Duplicate space"
            closeDialog={closeDuplicateDialog}
            initialSpace={fullSpace}
            spacesBasePath={spacesBasePath}
          />
        ))}
      {visibleDialog === 'report' && reportUrl && (
        <ReportSpaceDialog
          reportUrl={reportUrl}
          onClose={() => {
            onAction?.('COPILOT_SPACE_REPORT_DIALOG_CLOSE')
            setVisibleDialog(null)
          }}
          returnFocusRef={anchorRef}
        />
      )}
    </>
  )
}

export function DeleteSpaceDialog({
  onConfirm,
  onCancel,
  returnFocusRef,
}: {
  onConfirm: () => Promise<void>
  onCancel: () => void
  returnFocusRef?: React.RefObject<HTMLElement | null> | null
}) {
  const [loading, setLoading] = useState(false)

  return (
    <ConfirmationDialog
      title="Delete space"
      onClose={async gesture => {
        if (loading) return
        if (gesture === 'confirm') {
          setLoading(true)
          try {
            await onConfirm()
          } finally {
            setLoading(false)
          }
        } else {
          onCancel()
          setTimeout(() => returnFocusRef?.current?.focus(), 0)
        }
      }}
      confirmButtonContent={loading ? <Spinner size="small" /> : 'Delete'}
      confirmButtonType="danger"
    >
      Are you sure you want to delete this space? This action cannot be undone.
    </ConfirmationDialog>
  )
}
